import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import propertyService from '../../../services/propertyService';
import Skeleton from '../../../components/ui/Skeleton';
import { useAuth } from '../../../contexts/AuthContext';
import { useToast } from '../../../components/ui/Toast';
import { addRecentlyViewed } from '../../../utils/recentlyViewed';

const propertyTypes = [
  {
    label: 'Rooms',
    icon: 'DoorOpen',
    categoryKey: 'room',
    query: { type: 'room', sort: 'newest', limit: 5 },
    searchUrl: '/advanced-search-discovery?type=room',
  },
  {
    label: 'Apartments',
    icon: 'Building',
    categoryKey: 'apartment',
    query: { type: 'flat', sort: 'newest', limit: 5 },
    fallbackQuery: { type: 'apartment', sort: 'newest', limit: 5 },
    searchUrl: '/advanced-search-discovery?type=flat',
  },
  {
    label: 'Houses',
    icon: 'Home',
    categoryKey: 'house',
    query: { type: 'house', sort: 'newest', limit: 5 },
    searchUrl: '/advanced-search-discovery?type=house',
  },
  {
    label: 'PG / Hostel',
    icon: 'Bed',
    categoryKey: 'pg',
    query: { type: 'pg', sort: 'newest', limit: 5 },
    fallbackQuery: { type: 'hostel', sort: 'newest', limit: 5 },
    searchUrl: '/advanced-search-discovery?type=pg',
  },
  {
    label: 'Hotels',
    icon: 'Building2',
    categoryKey: 'hotel',
    query: { type: 'hotel', sort: 'newest', limit: 5 },
    searchUrl: '/advanced-search-discovery?type=hotel',
  },
  {
    label: 'Office',
    icon: 'Briefcase',
    categoryKey: 'office',
    query: { search: 'office', sort: 'newest', limit: 5 },
    searchUrl: '/advanced-search-discovery?type=office',
  },
  {
    label: 'Shop',
    icon: 'Store',
    categoryKey: 'shop',
    query: { type: 'shop', sort: 'newest', limit: 5 },
    fallbackQuery: { search: 'shop', sort: 'newest', limit: 5 },
    searchUrl: '/advanced-search-discovery?type=shop',
  },
  {
    label: 'Warehouse',
    icon: 'Warehouse',
    categoryKey: 'warehouse',
    query: { type: 'warehouse', sort: 'newest', limit: 5 },
    fallbackQuery: { search: 'warehouse', sort: 'newest', limit: 5 },
    searchUrl: '/advanced-search-discovery?type=warehouse',
  },
];

// bhk is a string enum ('1' | '2' | '3' | '4+' | 'studio') — not a number.
const beds = (bhk) => {
  if (!bhk) return null;
  if (String(bhk).toLowerCase() === 'studio') return 'Studio';
  return `${bhk} Bed`;
};

const inr = (n) =>
  typeof n === 'number' && Number.isFinite(n)
    ? `₹${n.toLocaleString('en-IN')}`
    : null;

const normalizePhone = (rawPhone) => {
  if (!rawPhone) return null;
  const str = String(rawPhone).trim();
  const digits = str.replace(/\D/g, '');
  if (!digits || digits === '1234567890' || digits.length < 10) {
    return null;
  }
  if (digits.length === 10) {
    return { call: `+91${digits}`, wa: `91${digits}` };
  }
  if (digits.length === 12 && digits.startsWith('91')) {
    return { call: `+${digits}`, wa: digits };
  }
  return { call: str.startsWith('+') ? str : `+${digits}`, wa: digits };
};

const CategoryCard = ({ type, stat, loading, onSelect }) => {
  const { isAuthenticated } = useAuth();
  const toast = useToast();
  const navigate = useNavigate();

  const [imgFailed, setImgFailed] = useState(false);
  const image = (!imgFailed && stat?.image) ? stat.image : null;
  const phoneInfo = normalizePhone(stat?.phone);

  const handleCall = (e) => {
    e?.preventDefault();
    e?.stopPropagation();
    if (!isAuthenticated) {
      toast?.info?.('Please sign in with your email to call property owners.');
      const next = stat?.id ? `/property-detail-experience/${stat.id}` : window.location.pathname;
      navigate(`/user-registration-login?next=${encodeURIComponent(next)}`);
      return;
    }
    if (!phoneInfo?.call) {
      toast?.info?.('Owner contact number is not available for this listing. Please click to view details.');
      return;
    }
    window.location.href = `tel:${phoneInfo.call}`;
  };

  const handleWhatsApp = (e) => {
    e?.preventDefault();
    e?.stopPropagation();
    if (!isAuthenticated) {
      toast?.info?.('Please sign in with your email to message property owners on WhatsApp.');
      const next = stat?.id ? `/property-detail-experience/${stat.id}` : window.location.pathname;
      navigate(`/user-registration-login?next=${encodeURIComponent(next)}`);
      return;
    }
    if (!phoneInfo?.wa) {
      toast?.info?.('Owner WhatsApp number is not available for this listing. Please click to view details.');
      return;
    }
    const waMsg = encodeURIComponent(
      `Hi, I am interested in your property "${stat?.title || type.label}" listed on Toletforrent.`
    );
    window.open(`https://wa.me/${phoneInfo.wa}?text=${waMsg}`, '_blank', 'noopener,noreferrer');
  };

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={onSelect}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onSelect();
        }
      }}
      className="group relative text-left rounded-xl border border-gray-200/90 bg-white overflow-hidden hover:border-rose-300 hover:shadow-[0_8px_24px_-8px_rgba(225,29,72,0.16)] transition-all duration-300 cursor-pointer flex flex-col justify-between"
    >
      {/* Top Image Container */}
      <div className="relative aspect-[16/13] bg-gradient-to-br from-rose-50/50 via-slate-50 to-gray-100 overflow-hidden border-b border-gray-100/80">
        {image ? (
          <>
            <img
              src={image}
              alt={stat?.title || type?.label || 'Property'}
              loading="lazy"
              onError={() => setImgFailed(true)}
              className="w-full h-full object-cover group-hover:scale-105 transition duration-500 ease-out"
            />
            {/* Gradient shadow overlay only over real photos */}
            <div className="absolute inset-0 bg-gradient-to-t from-black/75 via-black/20 to-black/25 pointer-events-none" />
          </>
        ) : (
          <div className="absolute inset-0 flex flex-col items-center justify-center bg-gradient-to-br from-slate-50 via-rose-50/40 to-gray-100">
            <div className="w-9 h-9 rounded-lg bg-white shadow-xs border border-gray-200/70 flex items-center justify-center">
              <Icon name={type?.icon || 'Home'} size={18} className="text-rose-400" />
            </div>
            <span className="text-[9px] font-medium text-gray-400 mt-0.5">No photos added</span>
          </div>
        )}

        {/* Badges on top */}
        <div className="absolute top-2 inset-x-2 flex items-center justify-between pointer-events-none">
          <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[7.5px] font-bold bg-white/95 text-gray-900 shadow-xs backdrop-blur-xs tracking-wide uppercase border border-gray-200/60">
            New Listing
          </span>
        </div>

        {/* Category Label + Locality pinned to bottom of image */}
        <div className={`absolute bottom-2 left-2 right-2 pointer-events-none ${image ? 'text-white' : 'text-gray-800'}`}>
          <p className="text-xs sm:text-[13px] font-bold drop-shadow-sm flex items-center gap-1 leading-tight">
            <Icon name={type?.icon || 'Home'} size={13} className={image ? 'text-rose-300 shrink-0' : 'text-rose-500 shrink-0'} />
            <span className="truncate">{type?.label || 'Property'}</span>
          </p>
          {stat?.locality ? (
            <p className={`text-[10px] truncate flex items-center gap-1 mt-0.5 drop-shadow-sm ${image ? 'text-white/90' : 'text-gray-500'}`}>
              <Icon name="MapPin" size={10} className={image ? 'shrink-0 text-rose-300' : 'shrink-0 text-gray-400'} />
              <span className="truncate">{stat.locality}</span>
            </p>
          ) : null}
        </div>
      </div>

      {/* Card Body */}
      <div className="p-2.5 flex-1 flex flex-col justify-between">
        <div>
          {loading ? (
            <div className="space-y-1.5 py-0.5">
              <Skeleton className="h-3 w-24" />
              <Skeleton className="h-2.5 w-16" />
            </div>
          ) : stat ? (
            <>
              {/* Title of the single new listing */}
              <p className="text-[11.5px] font-semibold text-gray-800 line-clamp-1 group-hover:text-rose-600 transition">
                {stat.title || `New ${type?.label || 'Property'} Property`}
              </p>

              {/* Specs: Bed / Bath / Area */}
              <div className="flex items-center flex-wrap gap-x-1.5 gap-y-0.5 text-[10px] text-gray-500 mt-0.5">
                {beds(stat.bhk) && (
                  <span className="inline-flex items-center gap-0.5">
                    <Icon name="Bed" size={11} className="text-gray-400" />
                    {beds(stat.bhk)}
                  </span>
                )}
                {stat.bathrooms > 0 && (
                  <span className="inline-flex items-center gap-0.5">
                    <Icon name="Bath" size={11} className="text-gray-400" />
                    {stat.bathrooms} Bath
                  </span>
                )}
                {stat.area > 0 && (
                  <span className="inline-flex items-center gap-0.5">
                    <Icon name="Square" size={11} className="text-gray-400" />
                    {stat.area.toLocaleString('en-IN')} sqft
                  </span>
                )}
              </div>
            </>
          ) : (
            <p className="text-[11px] text-gray-400">Browse collection</p>
          )}
        </div>

        {/* Pricing & Contact Actions */}
        <div className="mt-2 pt-1.5 border-t border-gray-100 flex items-center justify-between gap-1.5">
          <div className="min-w-0">
            {stat?.rent ? (
              <p className="text-xs sm:text-[13px] font-bold text-gray-900 truncate">
                {inr(stat.rent)}
                <span className="text-[10px] font-normal text-gray-500 ml-0.5">
                  {stat.isHotel ? '/nt' : '/mo'}
                </span>
              </p>
            ) : (
              <span className="text-[10px] text-gray-400">View listing</span>
            )}
          </div>

          {/* Calling & WhatsApp Action Buttons without background color */}
          <div
            className="flex items-center gap-1 shrink-0"
            onClick={(e) => { e.preventDefault(); e.stopPropagation(); }}
          >
            <button
              type="button"
              onClick={handleCall}
              onKeyDown={(e) => e.stopPropagation()}
              disabled={loading}
              aria-label="Call Owner"
              className="w-7 h-7 inline-flex items-center justify-center rounded-full border border-gray-200 hover:border-rose-400 hover:bg-rose-50/50 text-gray-600 hover:text-rose-600 bg-transparent transition-all active:scale-95 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
              title={phoneInfo?.call ? (isAuthenticated ? `Call owner at ${phoneInfo.call}` : 'Sign in to call owner') : 'Contact owner on details page'}
            >
              <Icon name="Phone" size={12} className="shrink-0" />
            </button>

            <button
              type="button"
              onClick={handleWhatsApp}
              onKeyDown={(e) => e.stopPropagation()}
              disabled={loading}
              aria-label="Chat on WhatsApp"
              className="w-7 h-7 inline-flex items-center justify-center rounded-full border border-gray-200 hover:border-emerald-500 hover:bg-emerald-50/50 text-gray-600 hover:text-emerald-600 bg-transparent transition-all active:scale-95 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
              title={phoneInfo?.wa ? (isAuthenticated ? `Chat on WhatsApp with owner at ${phoneInfo.call}` : 'Sign in to chat on WhatsApp') : 'Contact owner on details page'}
            >
              <Icon name="WhatsApp" size={13} className="shrink-0 text-emerald-600" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const getCategoryMeta = (type) => {
  const t = String(type || '').toLowerCase().trim();
  switch (t) {
    case 'room':
      return { label: 'Rooms', icon: 'DoorOpen', searchUrl: '/advanced-search-discovery?type=room' };
    case 'flat':
    case 'apartment':
      return { label: 'Apartments', icon: 'Building', searchUrl: '/advanced-search-discovery?type=flat' };
    case 'house':
    case 'villa':
      return { label: 'Houses', icon: 'Home', searchUrl: '/advanced-search-discovery?type=house' };
    case 'pg':
    case 'hostel':
      return { label: 'PG / Hostel', icon: 'Bed', searchUrl: '/advanced-search-discovery?type=pg' };
    case 'hotel':
      return { label: 'Hotels', icon: 'Building2', searchUrl: '/advanced-search-discovery?type=hotel' };
    case 'office':
    case 'commercial':
      return { label: 'Office', icon: 'Briefcase', searchUrl: '/advanced-search-discovery?type=office' };
    case 'shop':
      return { label: 'Shop', icon: 'Store', searchUrl: '/advanced-search-discovery?type=shop' };
    case 'warehouse':
      return { label: 'Warehouse', icon: 'Warehouse', searchUrl: '/advanced-search-discovery?type=warehouse' };
    default:
      return { label: t ? t.charAt(0).toUpperCase() + t.slice(1) : 'Property', icon: 'Home', searchUrl: '/advanced-search-discovery' };
  }
};

const toListingCard = (listing) => {
  if (!listing) return null;
  const meta = getCategoryMeta(listing.type);
  const rawPhone = listing.contactNumber || listing.ownerId?.phone || listing.ownerId?.phoneNumber || listing.contactPhone || listing.phone;
  const phone = (rawPhone && String(rawPhone).trim() && String(rawPhone).trim() !== '1234567890')
    ? String(rawPhone).trim()
    : null;

  const rawImg = listing.primaryImage || (Array.isArray(listing.images) && listing.images[0]) || null;
  const image = (rawImg && typeof rawImg === 'string' && rawImg.trim()) ? rawImg.trim() : null;

  return {
    id: listing._id || listing.id,
    title: listing.title || `New ${meta.label} listing`,
    rent: listing.rent || listing.dailyPrice || null,
    isHotel: listing.type === 'hotel',
    image,
    locality: [String(listing.address || '').split(',')[0].trim(), listing.city]
      .filter(Boolean)
      .join(', ') || listing.city || '',
    city: listing.city || '',
    bhk: listing.bhk,
    bathrooms: listing.bathrooms || 0,
    area: listing.area || 0,
    phone,
    ownerName: listing.ownerId?.displayName || 'Owner',
    type: meta,
  };
};

const FALLBACK_LISTINGS = [
  {
    id: 'fb-room-1',
    title: 'Modern Studio Room in Gomti Nagar',
    rent: 8500,
    type: { label: 'Rooms', icon: 'DoorOpen', searchUrl: '/advanced-search-discovery?type=room' },
    image: 'https://images.unsplash.com/photo-1522708323590-d24dbb6b0267?auto=format&fit=crop&w=800&q=80',
    locality: 'Gomti Nagar, Lucknow',
    bhk: '1RK',
    bathrooms: 1,
    area: 280,
  },
  {
    id: 'fb-apt-1',
    title: 'Spacious 2BHK Luxury Apartment',
    rent: 18000,
    type: { label: 'Apartments', icon: 'Building', searchUrl: '/advanced-search-discovery?type=flat' },
    image: 'https://images.unsplash.com/photo-1502672260266-1c1ef2d93688?auto=format&fit=crop&w=800&q=80',
    locality: 'Hazratganj, Lucknow',
    bhk: '2BHK',
    bathrooms: 2,
    area: 1100,
  },
  {
    id: 'fb-house-1',
    title: 'Independent Villa House with Garden',
    rent: 32000,
    type: { label: 'Houses', icon: 'Home', searchUrl: '/advanced-search-discovery?type=house' },
    image: 'https://images.unsplash.com/photo-1600585154340-be6161a56a0c?auto=format&fit=crop&w=800&q=80',
    locality: 'Indira Nagar, Lucknow',
    bhk: '3BHK',
    bathrooms: 3,
    area: 1850,
  },
  {
    id: 'fb-pg-1',
    title: 'Fully Furnished PG with Meals & WiFi',
    rent: 6500,
    type: { label: 'PG / Hostel', icon: 'Bed', searchUrl: '/advanced-search-discovery?type=pg' },
    image: 'https://images.unsplash.com/photo-1555854877-bab0e564b8d5?auto=format&fit=crop&w=800&q=80',
    locality: 'Aliganj, Lucknow',
    bhk: '1 Bed',
    bathrooms: 1,
    area: 150,
  },
  {
    id: 'fb-office-1',
    title: 'Prime Commercial Office Space',
    rent: 45000,
    type: { label: 'Office', icon: 'Briefcase', searchUrl: '/advanced-search-discovery?type=office' },
    image: 'https://images.unsplash.com/photo-1497366216548-37526070297c?auto=format&fit=crop&w=800&q=80',
    locality: 'Vibhuti Khand, Lucknow',
    bhk: null,
    bathrooms: 2,
    area: 1200,
  },
  {
    id: 'fb-shop-1',
    title: 'Corner Retail Showroom / Shop',
    rent: 35000,
    type: { label: 'Shop', icon: 'Store', searchUrl: '/advanced-search-discovery?type=shop' },
    image: 'https://images.unsplash.com/photo-1582037928769-181f2644ecb7?auto=format&fit=crop&w=800&q=80',
    locality: 'Aminabad, Lucknow',
    bhk: null,
    bathrooms: 1,
    area: 650,
  },
  {
    id: 'fb-wh-1',
    title: 'Industrial Storage Warehouse',
    rent: 95000,
    type: { label: 'Warehouse', icon: 'Warehouse', searchUrl: '/advanced-search-discovery?type=warehouse' },
    image: 'https://images.unsplash.com/photo-1586528116311-ad8dd3c8310d?auto=format&fit=crop&w=800&q=80',
    locality: 'Transport Nagar, Lucknow',
    bhk: null,
    bathrooms: 2,
    area: 5000,
  },
  {
    id: 'fb-apt-2',
    title: 'Cozy Budget 1BHK Flat',
    rent: 9500,
    type: { label: 'Apartments', icon: 'Building', searchUrl: '/advanced-search-discovery?type=flat' },
    image: 'https://images.unsplash.com/photo-1560448204-e02f11c3d0e2?auto=format&fit=crop&w=800&q=80',
    locality: 'Jankipuram, Lucknow',
    bhk: '1BHK',
    bathrooms: 1,
    area: 550,
  },
  {
    id: 'fb-hotel-1',
    title: 'Premium Boutique Hotel Suite',
    rent: 2200,
    isHotel: true,
    type: { label: 'Hotels', icon: 'Building2', searchUrl: '/advanced-search-discovery?type=hotel' },
    image: 'https://images.unsplash.com/photo-1618773928121-c32242e63f39?auto=format&fit=crop&w=800&q=80',
    locality: 'Mahanagar, Lucknow',
    bhk: '1 Suite',
    bathrooms: 1,
    area: 350,
  },
  {
    id: 'fb-pg-2',
    title: 'AC Double Sharing Student PG',
    rent: 5500,
    type: { label: 'PG / Hostel', icon: 'Bed', searchUrl: '/advanced-search-discovery?type=pg' },
    image: 'https://images.unsplash.com/photo-1595526114035-0d45ed16cfbf?auto=format&fit=crop&w=800&q=80',
    locality: 'Kapurthala, Lucknow',
    bhk: 'Shared',
    bathrooms: 1,
    area: 200,
  },
  {
    id: 'fb-apt-3',
    title: '3BHK Penthouse with Terrace View',
    rent: 28000,
    type: { label: 'Apartments', icon: 'Building', searchUrl: '/advanced-search-discovery?type=flat' },
    image: 'https://images.unsplash.com/photo-1512917774080-9991f1c4c750?auto=format&fit=crop&w=800&q=80',
    locality: 'Shaheed Path, Lucknow',
    bhk: '3BHK',
    bathrooms: 3,
    area: 1600,
  },
  {
    id: 'fb-wh-2',
    title: 'Commercial Godown & Logistics Hub',
    rent: 140000,
    type: { label: 'Warehouse', icon: 'Warehouse', searchUrl: '/advanced-search-discovery?type=warehouse' },
    image: 'https://images.unsplash.com/photo-1587293852726-70cdb56c2866?auto=format&fit=crop&w=800&q=80',
    locality: 'Sarojini Nagar, Lucknow',
    bhk: null,
    bathrooms: 2,
    area: 8500,
  },
  {
    id: 'fb-room-2',
    title: 'Executive Furnished Room',
    rent: 9000,
    type: { label: 'Rooms', icon: 'DoorOpen', searchUrl: '/advanced-search-discovery?type=room' },
    image: 'https://images.unsplash.com/photo-1598928506311-c55ded91a20c?auto=format&fit=crop&w=800&q=80',
    locality: 'Charbagh, Lucknow',
    bhk: '1 Room',
    bathrooms: 1,
    area: 220,
  },
  {
    id: 'fb-shop-2',
    title: 'Road-Facing Main Market Shop',
    rent: 25000,
    type: { label: 'Shop', icon: 'Store', searchUrl: '/advanced-search-discovery?type=shop' },
    image: 'https://images.unsplash.com/photo-1567401893414-76b7b1e5a7a5?auto=format&fit=crop&w=800&q=80',
    locality: 'Alambagh, Lucknow',
    bhk: null,
    bathrooms: 1,
    area: 400,
  },
  {
    id: 'fb-house-2',
    title: 'Peaceful Residential Colony Floor',
    rent: 14000,
    type: { label: 'Houses', icon: 'Home', searchUrl: '/advanced-search-discovery?type=house' },
    image: 'https://images.unsplash.com/photo-1580587771525-78b9dba3b914?auto=format&fit=crop&w=800&q=80',
    locality: 'Rajajipuram, Lucknow',
    bhk: '2BHK',
    bathrooms: 2,
    area: 950,
  },
];

const POPULAR_CITIES = [
  'Lucknow',
  'Delhi NCR',
  'Noida',
  'Gurgaon',
  'Bengaluru',
  'Mumbai',
  'Pune',
  'Hyderabad',
  'Prayagraj',
  'Kanpur',
  'Varanasi',
  'Jaipur',
];

const NEARBY_CITIES_MAP = {
  lucknow: ['lucknow', 'kanpur', 'prayagraj', 'ayodhya'],
  kanpur: ['kanpur', 'lucknow', 'prayagraj', 'unnao'],
  prayagraj: ['prayagraj', 'varanasi', 'lucknow', 'kanpur'],
  varanasi: ['varanasi', 'prayagraj', 'ayodhya', 'lucknow'],
  delhi: ['delhi', 'noida', 'gurgaon', 'ghaziabad', 'faridabad'],
  noida: ['noida', 'delhi', 'gurgaon', 'greater noida', 'ghaziabad'],
  gurgaon: ['gurgaon', 'delhi', 'noida', 'faridabad'],
  mumbai: ['mumbai', 'thane', 'navi mumbai', 'pune'],
  pune: ['pune', 'mumbai', 'pcmc'],
  bengaluru: ['bengaluru', 'bangalore', 'mysuru'],
  hyderabad: ['hyderabad', 'secunderabad'],
  jaipur: ['jaipur', 'ajmer', 'delhi'],
  ahmedabad: ['ahmedabad', 'gandhinagar', 'vadodara'],
};

const LOCALITY_TO_CITY_MAP = {
  'gomti nagar': 'lucknow',
  'hazratganj': 'lucknow',
  'alambagh': 'lucknow',
  'indira nagar': 'lucknow',
  'aliganj': 'lucknow',
  'rajajipuram': 'lucknow',
  'chinhat': 'lucknow',
  'vibhav khand': 'lucknow',
  'ashiyana': 'lucknow',
  'vikas nagar': 'lucknow',
  'navi kot': 'lucknow',
  'jankipuram': 'lucknow',
  'mahanagar': 'lucknow',
  'swaroop nagar': 'kanpur',
  'civil lines': 'kanpur',
  'katra': 'prayagraj',
  'teachers colony': 'prayagraj',
  'koramangala': 'bengaluru',
  'indiranagar': 'bengaluru',
  'whitefield': 'bengaluru',
  'hsr layout': 'bengaluru',
  'andheri': 'mumbai',
  'bandra': 'mumbai',
  'powai': 'mumbai',
  'viman nagar': 'pune',
  'hinjewadi': 'pune',
  'gachibowli': 'hyderabad',
  'madhapur': 'hyderabad',
  'lajpat nagar': 'delhi',
  'dwarka': 'delhi',
  'rohini': 'delhi',
  'sector 62': 'noida',
  'cyber city': 'gurgaon',
  'vaishali nagar': 'jaipur',
};

const haversineKm = (lat1, lon1, lat2, lon2) => {
  if (lat1 == null || lon1 == null || lat2 == null || lon2 == null) return null;
  const R = 6371;
  const dLat = ((lat2 - lat1) * Math.PI) / 180;
  const dLon = ((lon2 - lon1) * Math.PI) / 180;
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos((lat1 * Math.PI) / 180) *
    Math.cos((lat2 * Math.PI) / 180) *
    Math.sin(dLon / 2) *
    Math.sin(dLon / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
};

const CARD_COUNT = 15; // 5 cards per row × 3 rows

const HeroSection = () => {
  const navigate = useNavigate();
  const [cards, setCards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCityModal, setShowCityModal] = useState(false);
  const [citySearchInput, setCitySearchInput] = useState('');
  const [locatingGps, setLocatingGps] = useState(false);

  // User location state
  const [locationState, setLocationState] = useState(() => {
    try {
      const saved = localStorage.getItem('tolet_user_location');
      if (saved) return JSON.parse(saved);
    } catch {
      /* ignore */
    }
    return { city: '', lat: null, lng: null, label: '', isGps: false };
  });

  // Load properties: strict proximity tiering so nearby/local properties appear first on top
  const loadCardsForLocation = async (loc) => {
    setLoading(true);
    try {
      const rawQuery = (loc?.label || loc?.city || '').trim().toLowerCase();

      // 1. Identify Target City & Locality
      let targetCity = '';
      for (const key of Object.keys(NEARBY_CITIES_MAP)) {
        if (rawQuery.includes(key)) {
          targetCity = key;
          break;
        }
      }
      if (!targetCity) {
        for (const [locKey, cityVal] of Object.entries(LOCALITY_TO_CITY_MAP)) {
          if (rawQuery.includes(locKey)) {
            targetCity = cityVal;
            break;
          }
        }
      }
      if (!targetCity && loc?.city) {
        targetCity = loc.city.toLowerCase().trim();
      }

      // Parallel queries: keyword text search, GPS radius, target city search, and newest platform fill
      const queries = [];

      // A) Keyword / Text Search (matches exact locality e.g. "gomti nagar", address, title)
      if (rawQuery) {
        queries.push(
          propertyService.search({ search: rawQuery, limit: CARD_COUNT }).catch(() => ({ properties: [] }))
        );
      }

      // B) GPS search if coordinates available
      if (loc?.lat != null && loc?.lng != null) {
        queries.push(
          propertyService.search({
            lat: loc.lat,
            lng: loc.lng,
            radiusKm: 75,
            sort: 'nearest',
            limit: CARD_COUNT,
          }).catch(() => ({ properties: [] }))
        );
      }

      // C) Target City properties search (fetch up to 50 newest properties of this city)
      if (targetCity) {
        queries.push(
          propertyService.search({ city: targetCity, sort: 'newest', limit: 50 }).catch(() => ({ properties: [] }))
        );
      }

      // D) Platform-wide newest properties to fill remaining slots after nearby cities
      queries.push(
        propertyService.search({ sort: 'newest', limit: 50 }).catch(() => ({ properties: [] }))
      );

      const results = await Promise.all(queries);

      // Collect all properties and deduplicate
      const candidateMap = new Map();
      for (const res of results) {
        const items = Array.isArray(res?.properties) ? res.properties : [];
        for (const item of items) {
          const id = String(item._id || item.id);
          if (id && !candidateMap.has(id)) {
            candidateMap.set(id, item);
          }
        }
      }

      // If targetCity was not detected from keywords, deduce from matched results
      if (!targetCity) {
        for (const item of candidateMap.values()) {
          const c = (item.city || '').toLowerCase().trim();
          if (c) {
            for (const key of Object.keys(NEARBY_CITIES_MAP)) {
              if (c.includes(key)) {
                targetCity = key;
                break;
              }
            }
            if (targetCity) break;
          }
        }
      }

      const nearbyCitiesList = targetCity ? (NEARBY_CITIES_MAP[targetCity] || []) : [];

      // Score each candidate property strictly by proximity tier
      const scoredCandidates = Array.from(candidateMap.values()).map((p) => {
        const pCity = (p.city || '').toLowerCase().trim();
        const pAddr = (p.address || '').toLowerCase().trim();
        const pTitle = (p.title || '').toLowerCase().trim();
        const pDesc = (p.description || '').toLowerCase().trim();
        const fullText = `${pCity} ${pAddr} ${pTitle} ${pDesc}`;

        // Compute physical distance if GPS coordinates exist on both
        let distKm = null;
        if (loc?.lat != null && loc?.lng != null && Array.isArray(p.location?.coordinates)) {
          const [pLng, pLat] = p.location.coordinates;
          distKm = haversineKm(loc.lat, loc.lng, pLat, pLng);
        }

        let tier = 4; // default: distant other cities

        // Tier 1: Exact locality or search keyword matched in address/title/city
        // E.g. "gomti nagar" matched in "Vibhav khand gomti nagar"
        const isExactLocality =
          rawQuery &&
          (fullText.includes(rawQuery) ||
            rawQuery
              .split(/\s+/)
              .filter((w) => w.length > 3)
              .every((w) => fullText.includes(w)));

        if (isExactLocality) {
          tier = 1;
        } else if (targetCity && (pCity.includes(targetCity) || pAddr.includes(targetCity))) {
          // Tier 2: Same City (e.g. other localities in Lucknow)
          tier = 2;
        } else if (targetCity && nearbyCitiesList.length > 0) {
          // Tier 3: Neighboring / Regional nearby city (e.g. Kanpur, Prayagraj)
          const nearIdx = nearbyCitiesList.findIndex((nc) => pCity.includes(nc));
          if (nearIdx !== -1) {
            tier = 3 + nearIdx * 0.1; // ordered by regional proximity
          }
        }

        return {
          property: p,
          tier,
          distKm,
          createdAt: new Date(p.createdAt || 0).getTime(),
        };
      });

      // Strict Sorting:
      // 1. Group by Tier: Tier 1 (Locality/Selected City) -> Tier 2 (Same City) -> Tier 3 (Nearby Cities) -> Tier 4 (Other)
      // 2. Within the selected city, ALWAYS show newly added properties first (newest createdAt on top)
      // 3. For GPS mode within same tier, use physical km if valid coordinates exist
      scoredCandidates.sort((a, b) => {
        if (a.tier !== b.tier) {
          return a.tier - b.tier;
        }

        // If user used GPS locator and both items have genuine non-zero coordinates
        const aHasValidGps = a.distKm != null && Math.abs(a.property?.location?.coordinates?.[0] || 0) > 0.1;
        const bHasValidGps = b.distKm != null && Math.abs(b.property?.location?.coordinates?.[0] || 0) > 0.1;
        if (loc?.isGps && aHasValidGps && bHasValidGps) {
          return a.distKm - b.distKm;
        }

        // Default & city selection: newest added properties on top
        return b.createdAt - a.createdAt;
      });

      // Map to listing cards
      const sortedCards = scoredCandidates.map((c) => toListingCard(c.property)).filter(Boolean);

      // Pad with fallbacks if needed
      const seenCardIds = new Set(sortedCards.map((c) => c.id));
      if (sortedCards.length < CARD_COUNT) {
        for (const fb of FALLBACK_LISTINGS) {
          if (sortedCards.length >= CARD_COUNT) break;
          if (!seenCardIds.has(fb.id)) {
            seenCardIds.add(fb.id);
            const adjustedFb = targetCity
              ? {
                ...fb,
                locality: `${fb.locality.split(',')[0]}, ${targetCity.charAt(0).toUpperCase() + targetCity.slice(1)
                  }`,
              }
              : fb;
            sortedCards.push(adjustedFb);
          }
        }
      }

      setCards(sortedCards.slice(0, CARD_COUNT));
    } catch {
      setCards(FALLBACK_LISTINGS.slice(0, CARD_COUNT));
    } finally {
      setLoading(false);
    }
  };

  // Initial detection: Silent IP geolocation if no saved location
  useEffect(() => {
    let cancelled = false;

    const detectLocation = async () => {
      // If user already has a saved location in localStorage, use it directly
      if (locationState?.label) {
        loadCardsForLocation(locationState);
        return;
      }

      // Otherwise silently detect via IP
      try {
        const res = await fetch('https://ipapi.co/json/', { signal: AbortSignal.timeout(3000) });
        if (res.ok) {
          const data = await res.json();
          if (!cancelled && data?.city) {
            const detected = {
              city: data.city,
              lat: data.latitude || null,
              lng: data.longitude || null,
              label: data.city,
              isGps: false,
            };
            setLocationState(detected);
            try {
              localStorage.setItem('tolet_user_location', JSON.stringify(detected));
            } catch { }
            loadCardsForLocation(detected);
            return;
          }
        }
      } catch {
        /* fallback to default */
      }

      if (!cancelled) {
        const defaultLoc = { city: 'Lucknow', lat: null, lng: null, label: 'Lucknow', isGps: false };
        setLocationState(defaultLoc);
        loadCardsForLocation(defaultLoc);
      }
    };

    detectLocation();

    return () => {
      cancelled = true;
    };
  }, []);

  // Use precise GPS location on user action with reverse geocoding
  const requestGpsLocation = () => {
    if (!navigator.geolocation) {
      alert('Geolocation is not supported by your browser.');
      return;
    }

    setLocatingGps(true);
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        const { latitude, longitude } = pos.coords;
        let detectedLabel = 'Current Location';
        let detectedCity = '';

        try {
          // 1. Fast reverse geocoding via BigDataCloud
          const bdcRes = await fetch(
            `https://api.bigdatacloud.net/data/reverse-geocode-client?latitude=${latitude}&longitude=${longitude}&localityLanguage=en`,
            { signal: AbortSignal.timeout(3000) }
          );
          if (bdcRes.ok) {
            const data = await bdcRes.json();
            const city = data?.city || data?.locality || '';
            const locality = data?.locality && data.locality !== city ? data.locality : '';
            if (locality && city) {
              detectedLabel = `${locality}, ${city}`;
              detectedCity = city;
            } else if (city) {
              detectedLabel = city;
              detectedCity = city;
            }
          }
        } catch {
          // ignore
        }

        // 2. Backup reverse geocode if first attempt did not resolve city
        if (!detectedCity) {
          try {
            const res = await fetch(
              `https://nominatim.openstreetmap.org/reverse?lat=${latitude}&lon=${longitude}&format=json`,
              {
                headers: { 'User-Agent': 'ToletForRent/1.0' },
                signal: AbortSignal.timeout(3000),
              }
            );
            if (res.ok) {
              const data = await res.json();
              const addr = data?.address || {};
              const rawCity = addr.city || addr.town || addr.municipality || addr.city_district || addr.state_district || '';
              const locality = addr.suburb || addr.neighbourhood || addr.residential || '';
              const cleanCity = rawCity.replace(/district/i, '').trim();
              if (locality && cleanCity) {
                detectedLabel = `${locality}, ${cleanCity}`;
                detectedCity = cleanCity;
              } else if (cleanCity) {
                detectedLabel = cleanCity;
                detectedCity = cleanCity;
              }
            }
          } catch {
            // ignore
          }
        }

        const gpsLoc = {
          city: detectedCity,
          lat: latitude,
          lng: longitude,
          label: detectedLabel || 'Current Location',
          isGps: true,
        };
        setLocationState(gpsLoc);
        try {
          localStorage.setItem('tolet_user_location', JSON.stringify(gpsLoc));
        } catch { }
        setLocatingGps(false);
        setShowCityModal(false);
        loadCardsForLocation(gpsLoc);
      },
      (err) => {
        setLocatingGps(false);
        console.warn('GPS permission denied or timed out:', err);
        alert('Could not access GPS location. Please select your city from the list.');
      },
      { timeout: 8000, enableHighAccuracy: true },
    );
  };

  // User selects a city from modal or quick chip
  const handleSelectCity = (cityName) => {
    const newLoc = {
      city: cityName,
      lat: null,
      lng: null,
      label: cityName,
      isGps: false,
    };
    setLocationState(newLoc);
    try {
      localStorage.setItem('tolet_user_location', JSON.stringify(newLoc));
    } catch { }
    setShowCityModal(false);
    setCitySearchInput('');
    loadCardsForLocation(newLoc);
  };

  const handleSelectCard = (type, stat) => {
    if (stat?.id && !stat?.id?.startsWith?.('fb-')) {
      addRecentlyViewed(stat);
      navigate(`/property-detail-experience/${stat.id}`);
    } else if (type?.searchUrl) {
      const cityParam = locationState?.city ? `&city=${encodeURIComponent(locationState.city)}` : '';
      navigate(`${type.searchUrl}${cityParam}`);
    } else {
      const cityParam = locationState?.city ? `&city=${encodeURIComponent(locationState.city)}` : '';
      navigate(`/advanced-search-discovery?q=${encodeURIComponent(type?.label || '')}${cityParam}`);
    }
  };

  const filteredCities = POPULAR_CITIES.filter((c) =>
    c.toLowerCase().includes(citySearchInput.trim().toLowerCase()),
  );

  return (
    <section className="bg-white pt-4 sm:pt-6 lg:pt-8 pb-8">
      <div className="max-w-[1360px] mx-auto px-4 sm:px-6 lg:px-8 xl:px-12">
        {/* Headline */}
        <div className="text-center max-w-3xl mx-auto">
          <h1 className="text-2xl min-[480px]:text-3xl sm:text-4xl lg:text-5xl font-extrabold text-gray-900 leading-tight tracking-tight text-balance">
            Find your next place to live <span className="text-rose-500">— without the broker</span>
          </h1>
          <p className="mt-3 sm:mt-4 text-sm sm:text-base lg:text-lg text-gray-600 max-w-2xl mx-auto">
            Verified rooms, PGs, flats, warehouses and commercial spaces across 100+ Indian cities. Connect directly with owners.
          </p>

          {/* Clean, Modern User Location Access Bar */}
          <div className="mt-5 flex items-center justify-center">
            {/* Unified Location Capsule */}
            <div className="inline-flex items-center rounded-full bg-white border border-gray-200/90 shadow-xs hover:border-gray-300 hover:shadow-sm transition-all p-1 pl-3 pr-1.5 max-w-full">
              {/* City selector button */}
              <button
                type="button"
                onClick={() => setShowCityModal(true)}
                className="inline-flex items-center gap-2 py-1 pr-3 text-left group cursor-pointer transition min-w-0"
                title="Change city or search area"
              >
                <span className="w-6 h-6 rounded-full bg-rose-50 text-rose-500 flex items-center justify-center group-hover:bg-rose-100 group-hover:scale-105 transition shrink-0">
                  <Icon name="MapPin" size={13} />
                </span>
                <span className="text-xs sm:text-sm font-semibold text-gray-800 group-hover:text-rose-600 transition truncate max-w-[140px] min-[380px]:max-w-[200px] sm:max-w-[280px]">
                  {locationState.label || 'Select Location'}
                </span>
                <Icon name="ChevronDown" size={13} className="text-gray-400 group-hover:text-gray-600 shrink-0 transition" />
              </button>

              {/* Vertical divider */}
              <div className="h-4 w-px bg-gray-200 shrink-0 mx-1" />

              {/* 1-Tap Detect GPS Location Button */}
              <button
                type="button"
                onClick={requestGpsLocation}
                disabled={locatingGps}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-rose-50 hover:bg-rose-100 text-rose-700 text-xs font-semibold transition active:scale-95 cursor-pointer disabled:opacity-60 shrink-0"
                title="Detect properties near your current GPS position"
              >
                <Icon
                  name="Navigation"
                  size={12}
                  className={`shrink-0 ${locatingGps ? 'animate-spin text-rose-600' : 'text-rose-500'}`}
                />
                <span className="hidden min-[360px]:inline">{locatingGps ? 'Locating…' : 'Locate Me'}</span>
                <span className="inline min-[360px]:hidden">{locatingGps ? '…' : 'GPS'}</span>
              </button>
            </div>
          </div>
        </div>

        {/* Header above cards grid showing active location context */}
        <div className="mt-8 flex items-center justify-between pb-2.5 border-b border-gray-100">
          <div className="flex items-center gap-2 flex-wrap">
            <Icon name="MapPin" size={16} className="text-rose-500 shrink-0" />
            <h2 className="text-sm sm:text-base font-bold text-gray-900 tracking-tight flex items-center gap-1.5 flex-wrap">
              <span>Properties in & Near</span>
              <span className="text-rose-600 font-extrabold">{locationState.label || 'Your Location'}</span>
            </h2>
          </div>
        </div>

        {/* Category cards: 5 cards in each row, 3 rows (15 cards total) */}
        <div className="mt-4 grid grid-cols-1 min-[420px]:grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3 sm:gap-3.5">
          {loading
            ? Array.from({ length: CARD_COUNT }).map((_, i) => (
              <div key={i} className="rounded-xl border border-gray-200/90 bg-white p-2.5 space-y-2">
                <Skeleton className="aspect-[16/11] rounded-lg w-full" />
                <Skeleton className="h-3.5 w-3/4" />
                <Skeleton className="h-2.5 w-1/2" />
              </div>
            ))
            : cards.slice(0, CARD_COUNT).map((card, i) => (
              <CategoryCard
                key={card.id || i}
                type={card.type}
                stat={card}
                loading={false}
                onSelect={() => handleSelectCard(card.type, card)}
              />
            ))}
        </div>

        {/* View all new listings button */}
        <div className="mt-10 flex flex-col items-center justify-center text-center">
          <button
            type="button"
            onClick={() =>
              navigate(
                locationState?.city
                  ? `/advanced-search-discovery?city=${encodeURIComponent(locationState.city)}`
                  : '/advanced-search-discovery',
              )
            }
            className="group relative inline-flex items-center gap-3 px-6 py-4 sm:px-8 sm:py-4 rounded-full text-sm sm:text-base font-bold text-white bg-gradient-to-r from-rose-600 via-rose-500 to-rose-600 hover:from-rose-500 hover:to-rose-700 shadow-[0_10px_25px_-4px_rgba(244,63,94,0.38)] hover:shadow-[0_16px_32px_-4px_rgba(244,63,94,0.48)] hover:-translate-y-0.5 active:translate-y-0 active:scale-[0.98] transition-all duration-300 cursor-pointer overflow-hidden border border-rose-400/30"
          >
            {/* Shimmer light sweep */}
            <span
              className="absolute inset-0 w-full h-full bg-gradient-to-r from-transparent via-white/20 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-1000 ease-out pointer-events-none"
              aria-hidden="true"
            />

            <div className="w-7 h-7 sm:w-8 sm:h-8 rounded-full bg-white/20 backdrop-blur-xs flex items-center justify-center shrink-0">
              <Icon name="Search" size={16} className="text-white" />
            </div>

            <span className="tracking-tight">
              {locationState?.label
                ? `Explore All Listings in ${locationState.label}`
                : 'Explore All Verified Listings'}
            </span>

            <Icon
              name="ArrowRight"
              size={18}
              className="transition-transform duration-300 group-hover:translate-x-1.5 shrink-0"
            />
          </button>

          <p className="mt-4 text-xs text-gray-500 font-medium">
            Browse rooms, flats, PGs & commercial spaces with smart filters & map view
          </p>
        </div>

        {/* Trust strip */}
        <div className="mt-14 grid grid-cols-2 sm:grid-cols-4 gap-3 max-w-5xl mx-auto">
          {[
            { value: '₹0', label: 'Brokerage — ever' },
            { value: 'Verified', label: 'Owner listings' },
            { value: 'Direct', label: 'Owner contact' },
            { value: 'Pan-India', label: 'Cities across India' },
          ].map((s, i) => (
            <div key={i} className="text-center p-3.5 rounded-xl border border-gray-200/80 bg-gray-50/40 sm:border-0 sm:bg-transparent sm:p-0">
              <p className="text-base sm:text-xl font-bold text-gray-900">{s.value}</p>
              <p className="text-[10px] sm:text-xs text-gray-500 mt-0.5">{s.label}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Select City Modal */}
      {showCityModal && (
        <div
          className="fixed inset-0 z-[1200] flex items-center justify-center p-3 sm:p-4 bg-black/50"
          onClick={() => setShowCityModal(false)}
        >
          <div
            className="bg-white rounded-2xl max-w-lg w-full p-4 sm:p-6 shadow-2xl border border-gray-100 animate-in fade-in zoom-in-95 duration-200 max-h-[90vh] flex flex-col overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between pb-3 sm:pb-4 border-b border-gray-100 shrink-0">
              <div>
                <h3 className="text-base sm:text-lg font-bold text-gray-900">Choose Your Location</h3>
                <p className="text-xs text-gray-500 mt-0.5">Find rooms, flats & commercial spaces nearby</p>
              </div>
              <button
                type="button"
                onClick={() => setShowCityModal(false)}
                className="w-8 h-8 rounded-full bg-gray-100 hover:bg-gray-200 flex items-center justify-center text-gray-500 hover:text-gray-700 transition cursor-pointer"
              >
                <Icon name="X" size={16} />
              </button>
            </div>

            <div className="overflow-y-auto flex-1 pr-1 py-3 space-y-4">
              {/* GPS Option */}
              <button
                type="button"
                onClick={requestGpsLocation}
                disabled={locatingGps}
                className="w-full flex items-center justify-between p-3 rounded-xl border border-emerald-200 bg-emerald-50/70 hover:bg-emerald-50 text-emerald-800 transition active:scale-[0.99] cursor-pointer"
              >
                <div className="flex items-center gap-2.5">
                  <div className="w-8 h-8 rounded-full bg-emerald-500 text-white flex items-center justify-center shrink-0">
                    <Icon name="Navigation" size={15} className={locatingGps ? 'animate-spin' : ''} />
                  </div>
                  <div className="text-left">
                    <p className="text-xs sm:text-sm font-bold text-emerald-950">
                      {locatingGps ? 'Detecting your GPS location…' : 'Use Current GPS Location'}
                    </p>
                    <p className="text-[11px] text-emerald-700">Find nearest properties within 50 km</p>
                  </div>
                </div>
                <Icon name="ChevronRight" size={16} className="text-emerald-600" />
              </button>

              {/* City search input */}
              <div className="relative">
                <Icon name="Search" size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  value={citySearchInput}
                  onChange={(e) => setCitySearchInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && citySearchInput.trim()) {
                      handleSelectCity(citySearchInput.trim());
                    }
                  }}
                  placeholder="Type city or area name (e.g. Lucknow, Noida)..."
                  className="w-full pl-9 pr-8 py-2.5 rounded-xl border border-gray-200 text-xs sm:text-sm focus:border-rose-400 focus:outline-none"
                  autoFocus
                />
                {citySearchInput && (
                  <button
                    type="button"
                    onClick={() => setCitySearchInput('')}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    <Icon name="X" size={14} />
                  </button>
                )}
              </div>

              {/* If custom city typed that isn't in popular */}
              {citySearchInput.trim() && !filteredCities.some((c) => c.toLowerCase() === citySearchInput.trim().toLowerCase()) && (
                <button
                  type="button"
                  onClick={() => handleSelectCity(citySearchInput.trim())}
                  className="w-full text-left px-3.5 py-2.5 rounded-xl bg-rose-50 text-rose-700 hover:bg-rose-100 text-xs font-semibold flex items-center justify-between border border-rose-200 cursor-pointer"
                >
                  <span className="truncate">Search properties in "{citySearchInput.trim()}"</span>
                  <Icon name="ArrowRight" size={14} className="shrink-0 ml-2" />
                </button>
              )}

              {/* Popular cities grid */}
              <div>
                <p className="text-[11px] font-bold text-gray-400 uppercase tracking-wider mb-2">Popular Cities</p>
                <div className="grid grid-cols-2 min-[420px]:grid-cols-3 gap-2">
                  {filteredCities.map((c) => {
                    const isSelected = (locationState?.city || locationState?.label)?.toLowerCase() === c.toLowerCase();
                    return (
                      <button
                        key={c}
                        type="button"
                        onClick={() => handleSelectCity(c)}
                        className={`px-3 py-2 rounded-xl text-xs font-semibold text-left transition flex items-center justify-between border cursor-pointer ${isSelected
                            ? 'border-rose-500 bg-rose-50 text-rose-700 shadow-xs'
                            : 'border-gray-200 hover:border-rose-300 hover:bg-gray-50 text-gray-700'
                          }`}
                      >
                        <span className="truncate">{c}</span>
                        {isSelected && (
                          <Icon name="Check" size={13} className="text-rose-600 shrink-0 ml-1" />
                        )}
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </section>
  );
};

export default HeroSection;
